import { z } from 'zod';

import { roundRadiusFt, type FieldCircle } from '@/lib/maps/circle';
import type { MapBounds, MapPoint } from '@/lib/maps/types';

/**
 * A wire coordinate. The tracking realm spells it `lng` (backend `LatLng`); every rule module in
 * `src/lib/maps/` speaks `MapPoint` — `{ lat, lon }`. Renamed here, once, at the parse boundary, so
 * nothing downstream has to carry two spellings.
 */
const wirePointSchema = z
  .object({ lat: z.number(), lng: z.number() })
  .transform(({ lat, lng }): MapPoint => ({ lat, lon: lng }));

/**
 * A Boundary arrives as a GeoJSON Polygon — `[lng, lat]` positions, exterior ring first and closed.
 * Normalised to rings of `MapPoint`, which is what the Boundary overlay, hit-testing and bounds
 * derivation all read. One polygon, so **one** level of nesting: our `BoundaryRings` is
 * `MapPoint[][]`, unlike the admin twin's multipolygon `MapPoint[][][]`.
 *
 * No minimum ring length, deliberately: a malformed Boundary is a *geometric* failure, not a schema
 * one (113586 §1f), so a two-point ring parses and simply fails to draw. The repeated closing
 * position is kept — dropping it is the render layer's business, not the wire's.
 */
const boundarySchema = z
  .object({
    type: z.literal('Polygon'),
    coordinates: z.array(z.array(z.tuple([z.number(), z.number()]))),
  })
  .transform(({ coordinates }): MapPoint[][] =>
    coordinates.map((ring) => ring.map(([lon, lat]) => ({ lat, lon }))),
  );

/** A Crop Year month, `"YYYY-MM"`. Checked rather than taken as any string: the picker this feeds
 *  is a month range, so a day component could only ever be wrong. */
const cropMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected YYYY-MM');

const cropYearSchema = z.object({ start: cropMonthSchema, end: cropMonthSchema });

/** Circle mode's stored form, kept beside the materialised ring rather than derived from it, so a
 *  circle reopens as a circle. */
const fieldCircleSchema = z.object({ midPoint: wirePointSchema, radiusFt: z.number() });

export const FIELD_SHAPE_TYPES = ['polygon', 'circle'] as const;
export const FIELD_ORIGINS = ['drawn', 'imported'] as const;

const fieldBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  crop: z.string().nullable(),
  /** Independent of `crop` — either may be set without the other. */
  cropYear: cropYearSchema.nullable(),
  /**
   * The centroid's city and state, matched and rendered individually (the row reads "City, ST").
   * Either may be null — reverse-geocoding finds no incorporated place for open country.
   */
  city: z.string().nullable(),
  state: z.string().nullable(),
  origin: z.enum(FIELD_ORIGINS),
  shapeType: z.enum(FIELD_SHAPE_TYPES),
  /** The **edit** source of truth, in marked order; `boundary` is the **render** source. Neither
   *  is derived from the other. */
  points: z.array(wirePointSchema).nullable(),
  circle: fieldCircleSchema.nullable(),
  boundary: boundarySchema,
  /** Derived server-side and read-only; there is no client-side acreage at all. */
  acreage: z.number(),
  acreageUnit: z.literal('acres'),
  deviceCount: z.number(),
  /**
   * ISO timestamp, load-bearing twice over: it orders the Field panel's previous/next stepping
   * (113586 §4k, derived client-side because the response carries no `navigation` block), and it
   * resolves a click on an edge two Fields share to the more recently created (§3i).
   */
  createdAt: z.string(),
  updatedAt: z.string(),
});

/** One row of the Boundary layer. Drops `devices` and `location`: pins come from the devices
 *  endpoint and the location label is only shown on the details panel. */
export const mapFieldSchema = fieldBaseSchema;

/**
 * A Field the server could not render. Named so the notice can say *which* Field rather than "a
 * field" (113586 §1f). `reason` is left an open string — the notice does not branch on it, and a
 * new server-side reason must not take the whole Boundary layer down.
 */
const skippedFieldSchema = z.object({
  id: z.string(),
  name: z.string(),
  reason: z.string(),
});

export const fieldListResponseSchema = z.object({
  data: z.array(mapFieldSchema),
  meta: z.object({
    totalItems: z.number(),
    /** Defaults to `[]`: a server with nothing to skip may omit the key entirely rather than send
     *  an empty array, and that must not fail the whole Boundary layer. */
    skipped: z.array(skippedFieldSchema).catch([]),
  }),
});

/**
 * The details panel's Field. Carries **no `navigation` block and no `bounds`** (verified against the
 * deployed API, 2026-08-25): stepping order comes from the list's `createdAt`, and bounds are
 * computed from `boundary`, both client-side.
 */
export const fieldDetailSchema = fieldBaseSchema.extend({
  /** "County, ST", reverse-geocoded from the centroid. Display-only free text — the panel's
   *  "City, State" line comes from `city` / `state`, not from here. */
  location: z.string().nullable(),
  devices: z.array(z.object({ id: z.string(), label: z.string(), status: z.string().optional() })),
});

export type MapField = z.infer<typeof mapFieldSchema>;
export type FieldDetail = z.infer<typeof fieldDetailSchema>;
export type FieldListResponse = z.infer<typeof fieldListResponseSchema>;
export type SkippedField = z.infer<typeof skippedFieldSchema>;
export type CropYear = z.infer<typeof cropYearSchema>;
export type FieldShapeType = (typeof FIELD_SHAPE_TYPES)[number];
export type FieldOrigin = (typeof FIELD_ORIGINS)[number];

/**
 * The `FieldErrorCode` values this app branches on — the backend enum is longer, and the rest are
 * import/save codes other tickets own. Per ADR-0010 a code constant is what we branch on, never a
 * raw string.
 */
export const FIELD_ERROR_CODES = {
  outsideUsa: 'FIELD_OUTSIDE_USA',
  overlapsExisting: 'FIELD_OVERLAPS_EXISTING',
  duplicatePoint: 'FIELD_DUPLICATE_POINT',
  /** Set on the ring verdict when a vertex already failed, so the ring was never tested. Carries an
   *  empty `message`, which is exactly why it must never be surfaced as a refusal reason. */
  shapeNotEvaluated: 'FIELD_SHAPE_NOT_EVALUATED',
} as const;

/**
 * One **Placement verdict**. `errorCode` is left an open string rather than an enum: a code the
 * backend adds later must not fail the parse and take marking down — we branch on the codes above
 * and otherwise show `message`, which the server always sends alongside.
 *
 * `conflictingFieldId` / `conflictingFieldName` / `duplicateOfIndex` are on the wire and deliberately
 * unmodelled: nothing here names the conflicting Field, and zod drops unknown keys. Ticket 09's
 * coordinate rows add `inlineMessage`, which differs from `message` for the USA rule only.
 */
const placementVerdictSchema = z.object({
  valid: z.boolean(),
  errorCode: z.string().nullable(),
  message: z.string().nullable(),
  /**
   * The same refusal in the **coordinate row's** voice. It differs from `message` for the USA rule
   * only — "Enter coordinates within the USA." beside an input, "Field must be within the USA." in a
   * toast — which is why both are on the wire and both are read. `.nullable()` with a `.catch`-free
   * default: a server that omits it falls back to `message` at the call site, not here.
   */
  inlineMessage: z.string().nullable().optional(),
});

const pointVerdictSchema = placementVerdictSchema.extend({ index: z.number() });

/**
 * The circle's own verdict. **Two verdicts, because a Grower fixes two things independently**: the
 * Mid Point's (which is really the whole disc's — the server samples the Mid Point plus the rim and
 * reports the first failure here) and the Radius's.
 *
 * The `radius` half is in practice always `valid`: the backend's `CircleDto` rejects an
 * out-of-range `radiusFt` in its validation pipe, before the service that would fail this verdict
 * ever runs. It is read anyway rather than dropped — reading what the server sends is ADR-0018's
 * rule, and the alternative is a client that silently ignores a refusal.
 */
const circleVerdictSchema = z.object({
  valid: z.boolean(),
  midPoint: placementVerdictSchema,
  radius: placementVerdictSchema,
});

/**
 * `POST /fields/tracking/validate-boundary`'s reply. Three verdicts because three things fail
 * independently: `results` a corner, `shape` the ring's own area and edges — which clear corners do
 * **not** guarantee — and `circle` the whole disc.
 */
export const boundaryValidationSchema = z.object({
  valid: z.boolean(),
  results: z.array(pointVerdictSchema),
  /** null below 3 **Point**s — there is no enclosed area yet — and in circle **Shape mode**. */
  shape: placementVerdictSchema.nullable(),
  /**
   * null in polygon mode. `.default(null)` rather than a bare `.nullable()`: a reply that omits the
   * key entirely means the same thing as one that sends null, and failing the parse over it would
   * take the whole marking path down over a field nothing branches on.
   */
  circle: circleVerdictSchema.nullable().default(null),
});

/** A wire coordinate, in the direction we SEND it. */
type WirePoint = { lat: number; lng: number };

/**
 * The candidate geometry a **Placement verdict** is asked about — one member per **Shape mode**.
 *
 * A circle sends **the circle itself**, not its 64 rendered ring points (grilling decision 15): the
 * server materialises the ring and samples the rim, so the rim it judges is the one it built rather
 * than one the client computed and it has to trust.
 */
export type ValidateBoundaryBody =
  | {
      shapeType: 'polygon';
      points: WirePoint[];
      /**
       * The Field being edited, so the server cannot raise an overlap against it. **Required, never
       * defaulted** (spec § Contract): `null` when creating, and a silent default is exactly how a
       * self-conflict bug comes back on ticket 14.
       */
      excludeFieldId: string | null;
    }
  | {
      shapeType: 'circle';
      circle: { midPoint: WirePoint; radiusFt: number };
      excludeFieldId: string | null;
    };

/** The other direction of the one rename `wirePointSchema` performs. The wire says `lng`; every
 *  `src/lib/maps/` module says `MapPoint`. One place, both directions. */
export function toWirePoint({ lat, lon }: MapPoint): WirePoint {
  return { lat, lng: lon };
}

/** {@link toWirePoint} for a whole circle — the same one rename, applied at the same boundary. */
export function toWireCircle({ midPoint, radiusFt }: FieldCircle): {
  midPoint: WirePoint;
  radiusFt: number;
} {
  // Rounded here too, through the same `roundRadiusFt`, so the body is valid by CONSTRUCTION rather
  // than by every producer of a Radius remembering: `CircleDto`'s `maxDecimalPlaces: 2` rejects
  // anything finer with a 422, which surfaces as a failed placement check rather than a verdict.
  // One rule, applied at the wire boundary as well as at its source — not a second copy of it.
  return { midPoint: toWirePoint(midPoint), radiusFt: roundRadiusFt(radiusFt) };
}

export type PlacementVerdict = z.infer<typeof placementVerdictSchema>;
export type BoundaryValidation = z.infer<typeof boundaryValidationSchema>;
export type CircleVerdict = z.infer<typeof circleVerdictSchema>;

/**
 * Copy the Add Field Details modal shows, as FE constants (ADR-0010: branch on a constant, never a
 * raw string). `duplicateName` mirrors the 409 the contract raises rather than reading the envelope,
 * so the wording is ours whatever the server sends.
 */
export const FIELD_DETAILS_MESSAGES = {
  invalidName: 'Please enter a valid name (2 - 50) characters.',
  duplicateName: 'A field with this name already exists.',
} as const;

/** The FE constant `<MutationToaster>` announces on a successful create (grilling Q14). */
export const FIELD_CREATED_MESSAGE = 'Field added successfully.';

export const FIELD_NAME_MIN = 2;
export const FIELD_NAME_MAX = 50;
export const CROP_MAX = 30;

/**
 * A Field Name as typed or pasted: letters, numbers, spaces and `- @ . # / \ : |` survive, and
 * everything else is dropped **silently** — no error, because a paste from a spreadsheet should just
 * work (113601). The 50-character rule is NOT applied here: over-length is an inline *error* the
 * Grower must see, not a silent truncation, which is the one place these two rules differ.
 */
export function stripFieldName(raw: string): string {
  return raw.replace(/[^A-Za-z0-9 \-@.#/\\:|]/g, '');
}

/**
 * The Field Name an **Imported shape** arrives with (ticket 09, grilling **Q13 / L13**).
 *
 * A `.dbf` name attribute is untrusted third-party text, so it goes through **exactly** the rule the
 * keyboard obeys — {@link stripFieldName} — and is then truncated to {@link FIELD_NAME_MAX}. The
 * truncation is the one place this differs from typing: an over-length *typed* name is an inline
 * error the Grower must see and correct, while an over-length *seed* is a value they never entered
 * and must not be blamed for.
 *
 * Below {@link FIELD_NAME_MIN} the seed is **empty**, which is the AC's "carries no name attribute,
 * or the attribute is empty" branch reached by a second road — so it needs no new state and no new
 * copy. Trimmed first, because the server trims but a whitespace-only attribute still reaches here.
 */
export function seedFieldName(suggested: string | null | undefined): string {
  const seeded = stripFieldName(suggested?.trim() ?? '').slice(0, FIELD_NAME_MAX);
  return seeded.length < FIELD_NAME_MIN ? '' : seeded;
}

/** A Crop: letters, numbers, spaces and hyphens, capped at 30. Unlike the name, the cap IS silent —
 *  113601 states it as part of what the field accepts, and gives no over-length message. */
export function stripCrop(raw: string): string {
  return raw.replace(/[^A-Za-z0-9 -]/g, '').slice(0, CROP_MAX);
}

/**
 * What the Add Field Details form holds. `crop` is `''` rather than null while typing — an antd
 * input's empty value — and the api layer nulls it. `cropYear` is the `["YYYY-MM", "YYYY-MM"]` tuple
 * `<CropYearField>` commits.
 */
export const fieldDetailsSchema = z.object({
  name: z
    .string()
    .min(FIELD_NAME_MIN, FIELD_DETAILS_MESSAGES.invalidName)
    .max(FIELD_NAME_MAX, FIELD_DETAILS_MESSAGES.invalidName),
  crop: z.string().max(CROP_MAX),
  cropYear: z.tuple([cropMonthSchema, cropMonthSchema]).nullable(),
});

export type FieldDetailsValues = z.infer<typeof fieldDetailsSchema>;

/**
 * `POST /fields/tracking` and `PATCH /fields/tracking/{fieldId}` — one body, two verbs, because a
 * create and an edit send exactly the same thing.
 *
 * A discriminated union for the same reason `ValidateBoundaryBody` is one: a polygon carries
 * `points` and a circle carries `circle`, and neither carries the other's key.
 *
 * `origin` carries the **Point allowance** and is required on **both** arms. Required rather than
 * optional because of what the server does on an update: it resolves
 * `input.origin ?? existing.origin` and demotes anything that is not `imported` to `drawn`, so a
 * body that sent `'drawn'` for an imported Field would take its 200-Point allowance away for good.
 * A required field means the resolution happens once, at the page, and no call site can omit it.
 * The circle arm carries it too and needs no branch — the server coerces `imported` + `circle` to
 * `drawn`, since an imported shape is always a polygon.
 */
export type SaveFieldBody =
  | {
      shapeType: 'polygon';
      origin: FieldOrigin;
      name: string;
      points: WirePoint[];
      crop: string | null;
      cropYear: CropYear | null;
    }
  | {
      shapeType: 'circle';
      origin: FieldOrigin;
      name: string;
      circle: { midPoint: WirePoint; radiusFt: number };
      crop: string | null;
      cropYear: CropYear | null;
    };

/** The FE constant `<MutationToaster>` announces on a successful edit (grilling Q14). No criterion
 *  specifies it — flagged to the BA as BA-5, non-blocking. */
export const FIELD_UPDATED_MESSAGE = 'Field updated successfully.';

/**
 * The write reply, parsed down to the **one** field we read. The rest of `FieldWriteResponse` —
 * including `releasedDeviceIds` — is deliberately dropped: the Fields and Devices queries are
 * invalidated rather than patched, so reproducing the server's association arithmetic client-side is
 * exactly what this app does not do (spec § Write path).
 */
export const fieldWriteResponseSchema = z.object({ id: z.string() });

/**
 * One **Imported shape** out of an uploaded shapefile — `POST /fields/tracking/import`'s per-record
 * shape, read from `greenway_api`'s `ImportedShape` @ `9f3a2bd7`. **Not** a Boundary: nothing has
 * been saved, it has no id, and the server stored nothing.
 *
 * `bounds` is transposed to `MapBounds` here so that nothing downstream ever holds two spellings of
 * the same box — the server's is the **tight** envelope by design and the viewport margin is the
 * client's (`IMPORT_FIT_PADDING`).
 *
 * **`kind` and `parentIndex` are on the wire and deliberately unmodelled.** A shapefile hole is
 * this glossary's **Interior shape** and comes back as an ordinary selectable entry; filtering one
 * client-side is forbidden, because `selectableCount` and `requiresSelection` are computed
 * server-side **over holes too** (grilling L12). zod drops what is not declared, so ignoring `kind`
 * is the whole of that decision.
 *
 * `unselectableReason.code` stays an open string, matching {@link placementVerdictSchema}'s
 * `errorCode`: a code the backend adds later must not fail the parse and take the preview down.
 */
const importedShapeSchema = z.object({
  /** The stable handle. **Never** the array position — a dropped row renumbers that, not this. */
  index: z.number(),
  origin: z.literal('imported'),
  /** Always `'polygon'`: a third-party "circle" arrives as a many-sided polygon. */
  shapeType: z.literal('polygon'),
  /** Distinct vertices in file order, WGS 84 — the closing vertex and consecutive duplicates are
   *  already removed server-side. Every **Point** the file supplied, with no client-side cap: a
   *  drawn Boundary's 50 never truncates an imported one. */
  points: z.array(wirePointSchema),
  pointCount: z.number(),
  bounds: z
    .object({
      minLat: z.number(),
      minLng: z.number(),
      maxLat: z.number(),
      maxLng: z.number(),
    })
    .transform(
      ({ minLat, minLng, maxLat, maxLng }): MapBounds => ({
        north: maxLat,
        south: minLat,
        east: maxLng,
        west: minLng,
      }),
    ),
  /** This record's `.dbf` name attribute, trimmed. Blank counts as absent server-side. Consumed by
   *  the Field Name pre-fill (ticket 09), not here. */
  suggestedName: z.string().nullable(),
  /** The CRS reprojected FROM; `null` when the set carried no `.prj`. Informational — `points` are
   *  always WGS 84. */
  sourceCrs: z.string().nullable(),
  assumedWgs84: z.boolean(),
  selectable: z.boolean(),
  unselectableReason: z.object({ code: z.string(), message: z.string() }).nullable().catch(null),
  /**
   * The **advisory** Placement verdict. **ADVISORY and NULLABLE — `null` means NOT EVALUATED, never
   * "valid"**: the server caps advisory containment at 10 selectable shapes, beyond which the
   * client must ask `validate-boundary` (ticket 06). `.default(null)` so an omitted key reads the
   * same as an explicit null.
   */
  placement: placementVerdictSchema.nullable().default(null),
});

/**
 * `POST /fields/tracking/import`'s reply. **The published spec declares no response schema for its
 * 200**, so this parses tolerantly and **drops a bad shape rather than emptying the preview** —
 * the posture `/devices/tracking/{id}/history` and the notifications feed already take, and the
 * reason `notificationPageEnvelopeSchema` reads rows through `safeParse`.
 *
 * `selectableCount === 0` is a **422**, never a 200, so no empty-preview branch exists anywhere
 * downstream (grilling L4).
 *
 * `fileName` and `setCount` are on the wire and unmodelled — nothing displays either.
 */
export const importedShapesResponseSchema = z.object({
  /** 200 — the **Point allowance** every imported shape carries for life. Read by the editor
   *  (ticket 08), carried through the store so it is the server's number and not a client copy. */
  maxPoints: z.number(),
  shapeCount: z.number(),
  selectableCount: z.number(),
  /** `selectableCount > 1` — the flag the "Multiple Fields Found!" confirmation reads (ticket 04). */
  requiresSelection: z.boolean(),
  shapes: z.array(z.unknown()).transform((rows) =>
    rows.flatMap((row) => {
      const parsed = importedShapeSchema.safeParse(row);
      return parsed.success ? [parsed.data] : [];
    }),
  ),
});

export type ImportedShape = z.infer<typeof importedShapeSchema>;
export type ImportedShapes = z.infer<typeof importedShapesResponseSchema>;

/** Verbatim from 113609, **no full stop** — unlike {@link FIELD_CREATED_MESSAGE}'s
 *  "Field added successfully.". Both are AC-pinned strings and silently reconciling their
 *  punctuation would be a product change (grilling Q18; flagged to the BA as BA-7). */
export const FIELD_UPLOADED_MESSAGE = 'Field uploaded successfully';

/** The import flow's single keyed toast slot, so a repeated outcome restarts its 5 s timer instead
 *  of stacking a second toast. Ticket 03's five failure paths share it. */
export const IMPORT_TOAST_KEY = 'field-import';

/** The import flow's toast lifetime, in seconds. Beside {@link IMPORT_TOAST_KEY} because the two
 *  always travel together: one slot, one duration, so a re-triggered outcome restarts the same
 *  timer instead of stacking. Shared by the feature's own mutation and by the Import preview page,
 *  which is why it is exported rather than private to either (ADR-0010). */
export const IMPORT_TOAST_SECONDS = 5;

/**
 * The "Discard Field Markings?" confirmation's copy — 113600's exact criteria wording, and
 * 113609's for **Placement mode**, which asks the identical question.
 *
 * **In the feature rather than in a page** ([ADR-0010](../../../docs/adr/0010-shared-constants-placement.md):
 * the narrowest scope all consumers share). Two pages now render this one dialog —
 * `<FieldMappingPage>` when leaving with a shape marked, `<FieldImportPage>` when leaving Placement
 * mode — and `eslint-plugin-boundaries` forbids one page importing another, so the shared scope has
 * moved off the page. The alternative was a third declaration of the same sentence, which is the
 * workaround `FIELDS_ERROR`/`DEVICES_ERROR` still carry a few lines above the old home
 * (`field-mapping.page.tsx`) and is exactly what this move is here to avoid repeating.
 *
 * The design (598:535931, and again 783:669434) draws "Cancel" / a filled-red "Delete"; ACs beat
 * designs (grilling Q7 / D1) and nothing here is being deleted — the markings were never saved — so
 * the labels are the criteria's and the CTA keeps its neutral treatment on **both** pages. Flagged
 * to the BA as BA-3, non-blocking. The labels themselves stay props at each call site: they are the
 * dialog component's own API, not this feature's vocabulary.
 */
export const DISCARD_TITLE = 'Discard Field Markings?';
export const DISCARD_BODY = "Any points you've marked will be lost.";
